/* eslint-disable */
import React, { useEffect, useState, useMemo } from 'react'
import mqtt from 'mqtt'
import axios from 'axios'
import { toast } from 'react-toastify'
import { Phone, Users, User, Clock, CheckCircle, XCircle, Search, RefreshCw, PlayCircle, PauseCircle, Activity, Ear, Mic, Hash, PhoneOff } from 'lucide-react'
import { getPredictiveMonitoringStats, resetPredictiveData, togglePredictiveStatus } from '../campaign-list/core/_requests'
import { Data, PredictiveMonitoringResponse, AgentStats } from '../campaign-list/core/_models'
import Swal from 'sweetalert2'
import * as signalR from '@microsoft/signalr'

export const MonitoringPredictive = ({ campaignId }: { campaignId?: string }) => {
  const [monitoringData, setMonitoringData] = useState<PredictiveMonitoringResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  // MQTT Live Agent States map: ext -> status
  const [agentLiveStatus, setAgentLiveStatus] = useState<Record<string, string>>({})
  
  // MQTT initialization
  useEffect(() => {
    const MQTT_URL = process.env.REACT_APP_MQTT_URL;
    if (!MQTT_URL) return;

    const client = mqtt.connect(`wss://${MQTT_URL}`, {
      clientId: `PredMonitor_${Math.random().toString(16).slice(2, 10)}`,
      username: 'user_asterisk',
      password: 'user_asterisk_123',
      reconnectPeriod: 5000,
      protocol: 'wss'
    });

    client.on('connect', () => {
      // Subscribe to per-extension status topic published by MonitoringWorker
      client.subscribe('dashboard/ext_status/+', (err) => {
        if (err) console.error('MQTT Subscribe error:', err);
      });
    });

    client.on('message', (topic, payload) => {
      if (topic.startsWith('dashboard/ext_status/')) {
        try {
          const rawData = JSON.parse(payload.toString());
          const extStr = rawData.ext?.toString();
          if (extStr) {
            setAgentLiveStatus(prev => ({ ...prev, [extStr]: rawData.status }));
          }
        } catch (e) {
          console.error("Error parsing MQTT", e);
        }
      }
    });

    return () => {
      client.end();
    };
  }, []);

  // Polling data for the selected campaign
  useEffect(() => {
    if (!campaignId) return;
    
    let isMounted = true;
    const fetchStats = async () => {
      try {
        if (!monitoringData) setLoading(true);
        const stats = await getPredictiveMonitoringStats(campaignId);
        if (isMounted) {
          setMonitoringData(stats);
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
        if (isMounted) setLoading(false);
      }
    };

    // Initial fetch
    fetchStats();

    // Use SignalR instead of polling
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiUrl}/statushub`, {
        skipNegotiation: true,
        transport: signalR.HttpTransportType.WebSockets,
        accessTokenFactory: () => {
          const raw = localStorage.getItem('kt-auth-react-v');
          if (!raw) return '';
          try {
            return JSON.parse(raw).api_token || '';
          } catch {
            return '';
          }
        }
      })
      .withAutomaticReconnect()
      .build();

    let fetchTimeout: any = null;

    connection.on('callStatusUpdated', (payload: any) => {
      if (String(payload.campaignId).toLowerCase() !== String(campaignId).toLowerCase()) return;
      
      // Debounce the fetch by 2 seconds to avoid spiking backend on burst calls
      if (fetchTimeout) clearTimeout(fetchTimeout);
      fetchTimeout = setTimeout(() => {
        if (isMounted) fetchStats();
      }, 2000);
    });

    let startPromise = connection.start().catch(e => console.warn('[Monitoring] SignalR hub error:', e));

    return () => {
      isMounted = false;
      if (fetchTimeout) clearTimeout(fetchTimeout);
      startPromise.then(() => connection.stop());
    };
  }, [campaignId]);

  const handleTelephonyAction = async (targetExt: string, mode: 'spy' | 'whisper' | 'barge') => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/call/telephony/action`, { 
        targetExt: targetExt, 
        mode: mode 
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('kt-auth-react-v') ? JSON.parse(localStorage.getItem('kt-auth-react-v') as string).api_token : ''}`
        }
      });
      toast.success(`${mode.toUpperCase()} initiated on Ext ${targetExt}. Connecting to your softphone...`);
    } catch (e: any) {
      console.error(e);
      Swal.fire('Error', e.response?.data?.message || 'Failed to initiate action', 'error');
    }
  };

  const formatSeconds = (secs: number) => {
    if (!secs) return '00:00:00';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'UP':
      case 'CALLING': return 'primary';
      case 'ONLINE':
      case 'IDLE': return 'success';
      case 'PAUSED':
      case 'AUX': return 'warning';
      case 'OFFLINE': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'UP': return 'Calling';
      case 'ONLINE': return 'Online';
      case 'IDLE': return 'Idle';
      case 'PAUSED': return 'AUX';
      case 'OFFLINE': return 'Offline';
      default: return status || 'Idle';
    }
  };

  const filteredAgents = useMemo(() => {
    if (!monitoringData?.agentStats) return [];
    return monitoringData.agentStats.filter(a => 
      a.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      String(a.extension).includes(searchTerm)
    );
  }, [monitoringData?.agentStats, searchTerm]);

  return (
    <div className='d-flex flex-column gap-7'>
      {monitoringData ? (
        <>
          {/* DASHBOARD CARDS */}
          <div className='row g-5 g-xl-8'>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-light-primary border border-primary border-opacity-25 shadow-sm'>
                <div className='card-body px-5 py-4 d-flex align-items-center justify-content-between'>
                  <div>
                    <div className='text-primary fw-bolder fs-1'>{monitoringData.totalData}</div>
                    <div className='text-gray-600 fw-bold fs-7'>Total Data</div>
                  </div>
                  <Users size={32} className='text-primary opacity-50' />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-light-info border border-info border-opacity-25 shadow-sm'>
                <div className='card-body px-5 py-4 d-flex align-items-center justify-content-between'>
                  <div>
                    <div className='text-info fw-bolder fs-1'>{monitoringData.callAttempt}</div>
                    <div className='text-gray-600 fw-bold fs-7'>Call Attempt</div>
                  </div>
                  <Phone size={32} className='text-info opacity-50' />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-light-success border border-success border-opacity-25 shadow-sm'>
                <div className='card-body px-5 py-4 d-flex align-items-center justify-content-between'>
                  <div>
                    <div className='text-success fw-bolder fs-1'>{monitoringData.totalCallUid}</div>
                    <div className='text-gray-600 fw-bold fs-7'>Total Call UID</div>
                  </div>
                  <CheckCircle size={32} className='text-success opacity-50' />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-light-warning border border-warning border-opacity-25 shadow-sm'>
                <div className='card-body px-5 py-4 d-flex align-items-center justify-content-between'>
                  <div>
                    <div className='text-warning fw-bolder fs-1'>{monitoringData.totalAgent}</div>
                    <div className='text-gray-600 fw-bold fs-7'>Total Agent</div>
                  </div>
                  <User size={32} className='text-warning opacity-50' />
                </div>
              </div>
            </div>
            
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-light-danger border border-danger border-opacity-25 shadow-sm'>
                <div className='card-body px-5 py-4 d-flex align-items-center justify-content-between'>
                  <div>
                    <div className='text-danger fw-bolder fs-1'>{monitoringData.onCall}</div>
                    <div className='text-gray-600 fw-bold fs-7'>On Call</div>
                  </div>
                  <Activity size={32} className='text-danger opacity-50' />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-light-dark border border-dark border-opacity-25 shadow-sm'>
                <div className='card-body px-5 py-4 d-flex align-items-center justify-content-between'>
                  <div>
                    <div className='text-dark fw-bolder fs-1'>{monitoringData.onQueue}</div>
                    <div className='text-gray-600 fw-bold fs-7'>On Queue</div>
                  </div>
                  <Clock size={32} className='text-dark opacity-50' />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-light-secondary border border-secondary border-opacity-25 shadow-sm'>
                <div className='card-body px-5 py-4 d-flex align-items-center justify-content-between'>
                  <div>
                    <div className='text-muted fw-bolder fs-1'>{monitoringData.totalAbandoned || 0}</div>
                    <div className='text-gray-600 fw-bold fs-7'>Abandoned</div>
                  </div>
                  <PhoneOff size={32} className='text-muted opacity-50' />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-light-secondary border border-secondary border-opacity-25 shadow-sm'>
                <div className='card-body px-5 py-4 d-flex align-items-center justify-content-between'>
                  <div>
                    <div className='text-gray-800 fw-bolder fs-1'>{monitoringData.remaining}</div>
                    <div className='text-gray-600 fw-bold fs-7'>Remaining</div>
                  </div>
                  <RefreshCw size={32} className='text-gray-800 opacity-50' />
                </div>
              </div>
            </div>
            <div className='col-xl-3 col-md-6'>
              <div className='card bg-white border border-gray-300 shadow-sm'>
                <div className='card-body px-5 py-4 d-flex align-items-center justify-content-between'>
                  <div>
                    <div className={`fw-bolder fs-1 text-${monitoringData.status === 'Running' ? 'success' : 'warning'}`}>
                      {monitoringData.status}
                    </div>
                    <div className='text-gray-600 fw-bold fs-7'>Status</div>
                  </div>
                  {monitoringData.status === 'Running' 
                    ? <PlayCircle size={32} className='text-success opacity-50' />
                    : <PauseCircle size={32} className='text-warning opacity-50' />
                  }
                </div>
              </div>
            </div>
          </div>

          {/* ACTIONS AND AGENT TABLE */}
          <div className='card shadow-sm' style={{ borderRadius: '16px' }}>
            <div className='card-header pt-5 pb-4'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Agent Information</span>
                <span className='text-muted mt-1 fw-bold fs-7'>Monitoring realtime agen pada campaign ini</span>
              </h3>
              <div className='card-toolbar gap-3'>
                <div className='position-relative me-2'>
                  <Search className='position-absolute top-50 translate-middle-y ms-3 text-muted' size={16} />
                  <input
                    type='text'
                    className='form-control form-control-sm form-control-solid ps-10'
                    placeholder='Cari agent...'
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className='card-body py-3'>
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  <thead>
                    <tr className='fw-bolder text-muted bg-light'>
                      <th className='ps-4 min-w-150px rounded-start'>Agent</th>
                      <th className='min-w-100px'>Status</th>
                      <th className='min-w-120px'>Last Call</th>
                      <th className='min-w-100px text-center'>Call Attempt</th>
                      <th className='min-w-100px text-center'>Call UID</th>
                      <th className='min-w-100px text-center'>Duration</th>
                      <th className='min-w-150px text-end pe-4 rounded-end'>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAgents.map((agent) => {
                      const extStr = String(agent.extension);
                      const liveStatus = agentLiveStatus[extStr] || 'IDLE'; // fallback to IDLE
                      
                      return (
                        <tr key={agent.agentId}>
                          <td className='ps-4'>
                            <div className='d-flex align-items-center'>
                              <div className='symbol symbol-40px me-3'>
                                <div className='symbol-label bg-light-primary text-primary fw-bolder'>
                                  {agent.agentName.charAt(0)}
                                </div>
                              </div>
                              <div className='d-flex justify-content-start flex-column'>
                                <span className='text-dark fw-bolder text-hover-primary mb-1 fs-6'>
                                  {agent.agentName}
                                </span>
                                <span className='text-muted fw-bold text-muted d-block fs-7'>
                                  <Hash size={12} className='me-1' />{agent.extension}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`badge badge-light-${getStatusColor(liveStatus)} fw-bold px-4 py-2`}>
                              {getStatusLabel(liveStatus)}
                            </span>
                          </td>
                          <td>
                            <span className='text-dark fw-bolder d-block fs-6'>
                              {agent.lastCall ? new Date(agent.lastCall).toLocaleTimeString() : '-'}
                            </span>
                          </td>
                          <td className='text-center'>
                            <span className='text-dark fw-bolder d-block fs-6'>{agent.totalCallAttempt}</span>
                          </td>
                          <td className='text-center'>
                            <span className='text-dark fw-bolder d-block fs-6'>{agent.totalCallUid}</span>
                          </td>
                          <td className='text-center'>
                            <span className='text-dark fw-bolder d-block fs-6'>{formatSeconds(agent.totalDuration)}</span>
                          </td>
                          <td className='text-end pe-4'>
                            <div className='d-flex justify-content-end gap-2'>
                              <button 
                                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                title='Spy (Listen Only)'
                                onClick={() => handleTelephonyAction(extStr, 'spy')}
                              >
                                <Ear size={16} />
                              </button>
                              <button 
                                className='btn btn-icon btn-bg-light btn-active-color-warning btn-sm me-1'
                                title='Whisper (Speak to Agent)'
                                onClick={() => handleTelephonyAction(extStr, 'whisper')}
                              >
                                <Mic size={16} />
                              </button>
                              <button 
                                className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm'
                                title='Barge (Speak to Both)'
                                onClick={() => handleTelephonyAction(extStr, 'barge')}
                              >
                                <Users size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredAgents.length === 0 && (
                      <tr>
                        <td colSpan={7} className='text-center py-8 text-muted'>
                          Tidak ada data agent untuk campaign ini.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className='d-flex justify-content-center align-items-center' style={{ height: '300px' }}>
          <div className='spinner-border text-primary' role='status'>
            <span className='visually-hidden'>Loading...</span>
          </div>
        </div>
      )}
    </div>
  )
}
